import React, { Suspense, useRef, useEffect } from 'react';
import { Canvas, useLoader, extend, useThree, useFrame } from '@react-three/fiber';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import * as THREE from 'three';
import Pillow from '@components/Tombstone/Pillow';
import MainStone from '@components/Tombstone/MainStone';
import BaseStone from '@components/Tombstone/BaseStone';
import blackTextureImg from '@static/stoneTextureBlack.jpg';
import grayTextureImg from '@static/stoneTextureGray.jpg';
import naturalTextureImg from '@static/stoneTextureNatural.jpg';

extend({ OrbitControls });

const Controls = ({ isMobile }) => {
  const { camera, gl } = useThree();
  const controlsRef = useRef();
  
  // Set up camera position and zoom - different for mobile vs desktop
  useEffect(() => {
    if (isMobile) {
      // Mobile: further back and rotated to face the user better
      camera.position.set(0, 2, 26);
      camera.lookAt(0, -2, 0);
    } else {
      // Desktop: closer for better detail
      camera.position.set(0, 0, 12);
      camera.lookAt(0, -2, 0);
    }
  }, [camera, isMobile]);
  
  useFrame(() => {
    if (controlsRef.current) {
      controlsRef.current.update();
    }
  });
  
  return (
    <orbitControls 
      ref={controlsRef} 
      args={[camera, gl.domElement]}
      minDistance={isMobile ? 20 : 8}
      maxDistance={isMobile ? 35 : 20}
      enablePan={false}
      target={[0, -2, 0]}
    />
  );
};

// Map color values to texture images
const getTextureForColor = (color, textures) => {
  if (color === 'natural' || color === '#f5f5dc' || color === '#d2b48c') {
    return textures.natural;
  }
  if (color === 'gray' || color === '#808080' || color === '#a9a9a9') {
    return textures.gray;
  }
  // Default to black for '#323131' or any other value
  return textures.black;
};

const Tombstone = ({ pillowColor, mainColor, baseColor }) => {
  // Detect if mobile
  const [isMobile, setIsMobile] = React.useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Load all textures upfront
  const blackTexture = useLoader(THREE.TextureLoader, blackTextureImg);
  const grayTexture = useLoader(THREE.TextureLoader, grayTextureImg);
  const naturalTexture = useLoader(THREE.TextureLoader, naturalTextureImg);

  const textures = {
    black: blackTexture,
    gray: grayTexture,
    natural: naturalTexture,
  };

  // Get appropriate texture for each part based on color
  const pillowTexture = getTextureForColor(pillowColor, textures);
  const mainTexture = getTextureForColor(mainColor, textures);
  const baseTexture = getTextureForColor(baseColor, textures);

  return (
    <div className="w-full h-full min-h-[500px]">
      <Canvas camera={{ position: [0, 2, isMobile ? 26 : 12], fov: isMobile ? 45 : 50 }}>
        <Controls isMobile={isMobile} />
        {/* Enhanced lighting setup */}
        <ambientLight intensity={0.6} />
        <directionalLight position={[5, 8, 5]} intensity={1.2} castShadow />
        <directionalLight position={[-5, 8, -5]} intensity={0.8} />
        <pointLight position={[10, 10, 10]} intensity={0.5} />
        <pointLight position={[-10, 10, -10]} intensity={0.5} />
        <Suspense fallback={null}>
          <Pillow texture={pillowTexture} />
          <MainStone texture={mainTexture} />
          <BaseStone texture={baseTexture} />
        </Suspense>
      </Canvas>
    </div>
  );
};

export default Tombstone;
